import React, { useState, useEffect, useCallback } from 'react';

// Status indicator component
const StatusIndicator = ({ status }) => {
  const statusConfig = {
    healthy: { color: '#22c55e', label: 'Healthy', pulse: false },
    degraded: { color: '#eab308', label: 'Degraded', pulse: true },
    warning: { color: '#f97316', label: 'Warning', pulse: true },
    down: { color: '#ef4444', label: 'Down', pulse: true },
    error: { color: '#ef4444', label: 'Error', pulse: true },
    checking: { color: '#3b82f6', label: 'Checking...', pulse: true },
    unknown: { color: '#6b7280', label: 'Unknown', pulse: false }
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          backgroundColor: config.color,
          boxShadow: config.pulse ? `0 0 0 0 ${config.color}` : 'none',
          animation: config.pulse ? 'pulse 2s infinite' : 'none'
        }}
      />
      <span style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        color: config.color,
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        {config.label}
      </span>
    </div>
  );
};

// Main SSHD Page Component
const SSHDPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  // Fetch SSH status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/sshd/status');
      const data = await response.json();
      setStatus(data);
      setLastCheck(new Date());
    } catch (error) {
      console.error('Failed to fetch SSH status:', error);
      setStatus({ status: 'error', error: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and periodic refresh
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Trigger check via Orbit
  const triggerCheck = async (useOrbit = false) => {
    setChecking(true);
    setCheckResult(null);
    try {
      const response = await fetch('/api/sshd/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger_orbit: useOrbit })
      });
      const data = await response.json();
      setCheckResult(data);
      // Refresh status after check
      await fetchStatus();
    } catch (error) {
      setCheckResult({ success: false, error: error.message });
    } finally {
      setChecking(false);
    }
  };

  // Trigger recovery
  const triggerRecovery = async () => {
    if (!confirm('This will attempt to recover the SSH service. Continue?')) {
      return;
    }
    setRecovering(true);
    setCheckResult(null);
    try {
      const response = await fetch('/api/sshd/recover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      setCheckResult(data);
      await fetchStatus();
    } catch (error) {
      setCheckResult({ success: false, error: error.message });
    } finally {
      setRecovering(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return date.toLocaleTimeString();
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: '#e5e5e5',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
          70% { box-shadow: 0 0 0 10px transparent; opacity: 0.7; }
          100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
        }
        .btn {
          padding: 12px 24px;
          border: 2px solid;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          transition: all 0.2s ease;
          background: transparent;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-primary {
          border-color: #3b82f6;
          color: #3b82f6;
        }
        .btn-primary:hover:not(:disabled) {
          background: #3b82f6;
          color: #000;
        }
        .btn-warning {
          border-color: #f97316;
          color: #f97316;
        }
        .btn-warning:hover:not(:disabled) {
          background: #f97316;
          color: #000;
        }
        .btn-danger {
          border-color: #ef4444;
          color: #ef4444;
        }
        .btn-danger:hover:not(:disabled) {
          background: #ef4444;
          color: #000;
        }
        .card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          padding: 24px;
        }
      `}</style>

      {/* Header */}
      <header style={{
        padding: '24px 48px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            margin: 0,
            letterSpacing: '-0.02em'
          }}>
            SSH Service Monitor
          </h1>
          <p style={{
            color: '#737373',
            margin: '4px 0 0 0',
            fontSize: '0.875rem'
          }}>
            Remote access service status and diagnostics
          </p>
        </div>
        <a
          href="/"
          style={{
            color: '#737373',
            textDecoration: 'none',
            fontSize: '0.875rem',
            padding: '8px 16px',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '4px'
          }}
        >
          ← Back to Home
        </a>
      </header>

      {/* Main Content */}
      <main style={{ padding: '48px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Status Card */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '24px'
          }}>
            <div>
              <h2 style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#737373',
                margin: '0 0 12px 0'
              }}>
                Service Status
              </h2>
              {loading ? (
                <StatusIndicator status="checking" />
              ) : (
                <StatusIndicator status={status?.status || 'unknown'} />
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#737373' }}>
                Last checked
              </div>
              <div style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                {formatTime(lastCheck)}
              </div>
            </div>
          </div>

          {/* Status Details */}
          {status && !loading && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              padding: '16px 0',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#737373', marginBottom: '4px' }}>
                  Service
                </div>
                <div style={{
                  color: status.service_active ? '#22c55e' : '#ef4444',
                  fontWeight: '600'
                }}>
                  {status.service_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#737373', marginBottom: '4px' }}>
                  Listening
                </div>
                <div style={{
                  color: status.listening ? '#22c55e' : '#ef4444',
                  fontWeight: '600'
                }}>
                  {status.listening ? 'Yes' : 'No'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#737373', marginBottom: '4px' }}>
                  Connections
                </div>
                <div style={{
                  color: status.accepting_connections ? '#22c55e' : '#ef4444',
                  fontWeight: '600'
                }}>
                  {status.accepting_connections ? 'Accepting' : 'Blocked'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Card */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: '#737373',
            margin: '0 0 16px 0'
          }}>
            Diagnostics
          </h2>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={() => triggerCheck(false)}
              disabled={checking || recovering}
            >
              {checking ? 'Checking...' : 'Quick Check'}
            </button>

            <button
              className="btn btn-warning"
              onClick={() => triggerCheck(true)}
              disabled={checking || recovering}
            >
              {checking ? 'Running...' : 'Deep Analysis (Orbit)'}
            </button>

            {status?.status !== 'healthy' && (
              <button
                className="btn btn-danger"
                onClick={triggerRecovery}
                disabled={checking || recovering}
              >
                {recovering ? 'Recovering...' : 'Attempt Recovery'}
              </button>
            )}
          </div>

          <p style={{
            fontSize: '0.75rem',
            color: '#525252',
            marginTop: '12px',
            marginBottom: 0
          }}>
            Deep Analysis uses Orbit AI agent for comprehensive diagnostics.
            Recovery will attempt automated service restoration.
          </p>
        </div>

        {/* Results Card */}
        {checkResult && (
          <div className="card">
            <h2 style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: '#737373',
              margin: '0 0 16px 0'
            }}>
              {checkResult.recovered !== undefined ? 'Recovery Result' : 'Check Result'}
            </h2>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: checkResult.success ? '#22c55e' : '#ef4444'
              }} />
              <span style={{ fontWeight: '600' }}>
                {checkResult.success ? 'Success' : 'Failed'}
              </span>
              {checkResult.method && (
                <span style={{
                  fontSize: '0.75rem',
                  color: '#737373',
                  padding: '2px 8px',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '4px'
                }}>
                  via {checkResult.method}
                </span>
              )}
            </div>

            {checkResult.message && (
              <p style={{ color: '#a3a3a3', margin: '0 0 12px 0' }}>
                {checkResult.message}
              </p>
            )}

            {checkResult.error && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '12px'
              }}>
                <span style={{ color: '#ef4444' }}>{checkResult.error}</span>
              </div>
            )}

            {checkResult.output && (
              <pre style={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                padding: '16px',
                borderRadius: '4px',
                fontSize: '0.8rem',
                fontFamily: 'JetBrains Mono, monospace',
                overflow: 'auto',
                maxHeight: '300px',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {checkResult.output}
              </pre>
            )}
          </div>
        )}

        {/* Recent Activity */}
        {status?.recent_activity && status.recent_activity.length > 0 && (
          <div className="card" style={{ marginTop: '24px' }}>
            <h2 style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: '#737373',
              margin: '0 0 16px 0'
            }}>
              Recent Activity
            </h2>
            <div style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '0.75rem',
              color: '#737373'
            }}>
              {status.recent_activity.map((line, i) => (
                <div key={i} style={{
                  padding: '4px 0',
                  borderBottom: i < status.recent_activity.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                }}>
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{
        padding: '24px 48px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        textAlign: 'center',
        color: '#525252',
        fontSize: '0.75rem'
      }}>
        SSH Service Monitor · Powered by Orbit Watchdog System
      </footer>
    </div>
  );
};

export default SSHDPage;
